import React, { useEffect, useState } from "react";
import { Button, Dialog, EditIcon, PlusIcon, TrashIcon } from "evergreen-ui";
import {
	Table,
	Card,
	Form,
	Input,
	message,
	Popconfirm,
	Button as AntButton,
} from "antd";
import FireFetch from "../../../Firebase/FireFetch";
import { TableExport } from "tableexport";
import { useListVals } from "../../../Firebase/databaseHooks";
import Firebase from "../../../Firebase/Firebase";
import {
	getDatabase,
	ref,
	update,
	push,
} from "firebase/database";
import TableActionMenu from "../../../Common/TableActionMenu";
import {
	getStorage,
	ref as storageRef,
	uploadBytes,
	getDownloadURL,
} from "firebase/storage";
import { MDBCol, MDBRow } from "mdbreact";

const db = getDatabase(Firebase);
const storage = getStorage(Firebase);

const LessonTypeLayout = (props) => {
	const canCreate = props.canCreate !== false;
	const canUpdate = props.canUpdate !== false;
	const canDelete = props.canDelete !== false;
	const [form] = Form.useForm();

	const columns = [
		{
			title: "Lesson Type Name",
			dataIndex: "typeName",
			key: "typeName",
			width: "30%",
			sorter: (a, b) => a.typeName?.localeCompare(b.typeName),
		},
		{
			title: "Description",
			dataIndex: "description",
			key: "description",
			width: "45%",
			render: (text) => text || "-",
		},
		{
			title: "Attachment (PDF)",
			dataIndex: "pdfUrl",
			key: "pdfUrl",
			width: "15%",
			render: (pdfUrl) =>
				pdfUrl ? (
					<a href={pdfUrl} target="_blank" rel="noopener noreferrer">
						View PDF
					</a>
				) : (
					"-"
				),
		},
		{
			title: "Action",
			dataIndex: "action",
			key: "action",
			width: "10%",
		},
	];

	const [typeArray, setTypeArray] = useState([]);
	const [showModal, setShowModal] = useState(false);
	const [arrayIn, loading] = useListVals(ref(db, "talmidsdb/lessonTypes"));
	const [selectedType, setSelectedType] = useState(null);
	const [pdfFile, setPdfFile] = useState(null);
	const [uploading, setUploading] = useState(false);

	useEffect(() => {
		if (arrayIn) {
			const tempArray = arrayIn.map((item, index) => ({
				key: item.id || index,
				id: item.id,
				typeName: item.typeName,
				description: item.description,
				pdfUrl: item.pdfUrl,
				action: (
					<TableActionMenu
						items={[
							canUpdate && {
								key: "edit",
								label: "Edit",
								onClick: () => {
									setSelectedType(item);
									setShowModal(true);
								},
							},
							canDelete && {
								key: "delete",
								label: "Delete",
								danger: true,
								onClick: () => {
									if (confirm("Delete this lesson type?")) {
										props.messageBack("lessonTypes", "delete", item.id);
									}
								},
							},
						]}
					/>
				),
			}));
			setTypeArray(tempArray);
		}
	}, [arrayIn, props, canUpdate, canDelete]);

	// Reset form when opening modal
	useEffect(() => {
		if (showModal) {
			form.setFieldsValue({
				typeName: selectedType?.typeName || "",
				description: selectedType?.description || "",
			});
			setPdfFile(null);
		}
	}, [showModal, selectedType, form]);

	const uploadPdfIfNeeded = async (id, currentUrl) => {
		if (!pdfFile) return currentUrl || null;

		const path = `lessonTypes/${id}/${pdfFile.name}`;
		const fileRef = storageRef(storage, path);
		await uploadBytes(fileRef, pdfFile);
		const url = await getDownloadURL(fileRef);
		return url;
	};

	const handleSubmit = async (values) => {
		if (selectedType && !canUpdate) {
			message.error("You do not have permission to update lesson types.");
			return;
		}

		if (!selectedType && !canCreate) {
			message.error("You do not have permission to create lesson types.");
			return;
		}

		try {
			setUploading(true);

			if (selectedType) {
				const id = selectedType.id;
				const pdfUrl = await uploadPdfIfNeeded(id, selectedType.pdfUrl);

				const object = {
					id,
					typeName: values.typeName,
					description: values.description || "",
					pdfUrl: pdfUrl || null,
				};

				await update(ref(db, "talmidsdb/lessonTypes/" + id), object);
				message.success("Lesson type updated successfully");
				FireFetch.addLog("LessonTypes", id, "Edit");
				props.messageBack && props.messageBack("lessonTypes", "edit", id);
			} else {
				// Create new lesson type
				const newRef = push(ref(db, "talmidsdb/lessonTypes"));
				const id = newRef.key;

				const pdfUrl = await uploadPdfIfNeeded(id, null);

				const object = {
					id,
					typeName: values.typeName,
					description: values.description || "",
					pdfUrl: pdfUrl || null,
				};

				await update(newRef, object);
				message.success("Lesson type created successfully");
				FireFetch.addLog("LessonTypes", id, "Create");
				props.messageBack && props.messageBack("lessonTypes", "create", id);
			}

			setShowModal(false);
		} catch (error) {
			message.error(
				"Unable to save Lesson Type due to an error: " + error.message
			);
		} finally {
			setUploading(false);
		}
	};

	const handleFileChange = (e) => {
		const file = e.target.files?.[0];
		if (file && file.type !== "application/pdf") {
			message.error("Only PDF files are allowed");
			return;
		}
		setPdfFile(file || null);
	};

	return (
		<div className="font-Nunito">
			<Dialog
				isShown={showModal}
				title={selectedType ? "Edit Lesson Type" : "New Lesson Type"}
				onCloseComplete={() => {
					setShowModal(false);
					setSelectedType(null);
				}}
				hasFooter={false}
				width="50%"
			>
				<MDBRow>
					<MDBCol className="px-4">
						<Form
							form={form}
							layout="vertical"
							onFinish={handleSubmit}
						>
							<Form.Item
								label="Lesson Type Name"
								name="typeName"
								rules={[
									{ required: true, message: "Please input lesson type name!" },
								]}
							>
								<Input
									type="text"
									placeholder="Enter lesson type name"
									className="w-100 mr-4"
								/>
							</Form.Item>

							<Form.Item
								label="Description (optional)"
								name="description"
							>
								<Input.TextArea
									rows={3}
									placeholder="Enter a short description (optional)"
									className="w-100 mr-4"
								/>
							</Form.Item>

							<Form.Item label="Attachment (PDF – optional)">
								{selectedType?.pdfUrl && (
									<div style={{ marginBottom: 8 }}>
										Current file:{" "}
										<a
											href={selectedType.pdfUrl}
											target="_blank"
											rel="noopener noreferrer"
										>
											View current PDF
										</a>
									</div>
								)}
								<Input
									type="file"
									accept="application/pdf"
									onChange={handleFileChange}
								/>
							</Form.Item>

							<AntButton
								type="primary"
								htmlType="submit"
								loading={uploading}
							>
								{selectedType ? "Save Changes" : "Create Lesson Type"}
							</AntButton>
						</Form>
					</MDBCol>
				</MDBRow>
			</Dialog>

			<div style={{ marginBottom: "16px" }}>
				{canCreate && (
					<Button
						appearance="primary"
						className="ml-3 my-2"
						onClick={() => {
							setSelectedType(null);
							setShowModal(true);
						}}
					>
						New Lesson Type <PlusIcon />
					</Button>
				)}
				<Button
					appearance="primary"
					className="mx-2"
					onClick={() => {
						FireFetch.exportXL(
							TableExport(document.getElementById("dataTable"), {
								filename: "Lesson Types",
								exportButtons: false,
								sheetname: "Lesson Types",
							}),
							typeArray
						);
					}}
				>
					Print
				</Button>
			</div>

			<Card className="d-flex flex-column">
				<Table
					style={{ overflow: "auto" }}
					loading={loading}
					pagination={{
						defaultPageSize: 100,
						showSizeChanger: true,
						pageSizeOptions: ["20", "50", "100"],
					}}
					id="dataTable"
					dataSource={typeArray}
					columns={columns}
					scroll={{ x: true }}
				/>
			</Card>
		</div>
	);
};

export default LessonTypeLayout;
